/**
 * Main layout component with navigation and header
 */
import React from "react";
import { Link, useLocation } from "react-router";
import {
  FileText,
  Settings,
  Users,
  BarChart3,
  Menu,
  X,
  GitBranch,
  Megaphone,
  CalendarX,
  FileCode2,
  Filter,
  Zap,
} from "lucide-react";
import CommandSearch from "../ui/CommandSearch";
import { useCommandSearch } from "../../hooks/useCommandSearch";

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const { isOpen, closeCommandSearch } = useCommandSearch();

  const navigation = [
    {
      name: "Flows",
      href: "/flows",
      icon: GitBranch,
      current:
        location.pathname.startsWith("/flows") || location.pathname === "/",
    },
    {
      name: "Variables",
      href: "/variables",
      icon: Settings,
      current: location.pathname.startsWith("/variables"),
    },
    {
      name: "Global Flows",
      href: "/node-groups",
      icon: GitBranch,
      current: location.pathname.startsWith("/node-groups"),
    },
    {
      name: "Actions",
      href: "/actions",
      icon: Zap,
      current: location.pathname.startsWith("/actions"),
    },
    {
      name: "Clients",
      href: "/clients",
      icon: Users,
      current: location.pathname.startsWith("/clients"),
    },
    {
      name: "Campaigns",
      href: "/campaigns",
      icon: Megaphone,
      current: location.pathname.startsWith("/campaigns"),
    },
    {
      name: "Dynamic Dates",
      href: "/quiet-periods",
      icon: CalendarX,
      current: location.pathname.startsWith("/quiet-periods"),
    },
    {
      name: "HTTP Configs",
      href: "/http-configs",
      icon: Settings,
      current: location.pathname.startsWith("/http-configs"),
    },
    {
      name: "HTTP Endpoints",
      href: "/http-endpoints",
      icon: Settings,
      current: location.pathname.startsWith("/http-endpoints"),
    },
    {
      name: "Templates",
      href: "/templates",
      icon: FileCode2,
      current: location.pathname.startsWith("/templates"),
    },
    {
      name: "Condition Rules",
      href: "/condition-rules",
      icon: Filter,
      current: location.pathname.startsWith("/condition-rules"),
    },
    {
      name: "Users",
      href: "/users",
      icon: Users,
      current: location.pathname.startsWith("/users"),
      disabled: false,
    },
    {
      name: "Settings",
      href: "/settings",
      icon: Settings,
      current: location.pathname.startsWith("/settings"),
      disabled: false,
    },
  ];

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar */}
      <div
        className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? "" : "hidden"}`}
      >
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75"
          onClick={() => setSidebarOpen(false)}
        />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 right-0 -mr-12 pt-2">
            <button
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-6 w-6 text-white" />
            </button>
          </div>
          <SidebarContent navigation={navigation} />
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <SidebarContent navigation={navigation} />
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top bar */}
        <div className="relative z-10 flex-shrink-0 flex h-16 bg-white shadow">
          <button
            className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 md:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6" />
          </button>
          <div className="flex-1 px-4 flex justify-between">
            <div className="flex-1 flex">
              <div className="w-full flex md:ml-0">
                <div className="flex items-center">
                  <h1 className="text-xl font-semibold text-gray-900">
                    Conversation Scripts Admin Panel
                  </h1>
                </div>
              </div>
            </div>
            <div className="ml-4 flex items-center md:ml-6 space-x-4">
              {/* Command Search Hint */}
              <div className="hidden md:flex items-center text-sm text-gray-500">
                <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">
                  Ctrl
                </kbd>
                <span className="mx-1">+</span>
                <kbd className="px-2 py-1 text-xs font-semibold text-gray-800 bg-gray-100 border border-gray-200 rounded">
                  K
                </kbd>
                <span className="ml-2">to search</span>
              </div>
              {/* Future: User menu */}
              <div className="text-sm text-gray-500">Admin User</div>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <div className="py-6">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8">
              {children}
            </div>
          </div>
        </main>
      </div>

      {/* Command Search */}
      <CommandSearch isOpen={isOpen} onClose={closeCommandSearch} />
    </div>
  );
};

interface SidebarContentProps {
  navigation: Array<{
    name: string;
    href: string;
    icon: any;
    current: boolean;
    disabled?: boolean;
  }>;
}

const SidebarContent: React.FC<SidebarContentProps> = ({ navigation }) => {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center h-16 flex-shrink-0 px-4 bg-gray-900">
        <div className="flex items-center">
          <FileText className="h-8 w-8 text-white" />
          <span className="ml-2 text-white font-bold">Admin Panel</span>
        </div>
      </div>
      <div className="flex-1 flex flex-col overflow-y-auto">
        <nav className="flex-1 px-2 py-4 bg-gray-800 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon;
            const baseClasses =
              "group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors duration-150";
            const activeClasses = "bg-gray-900 text-white";
            const inactiveClasses =
              "text-gray-300 hover:bg-gray-700 hover:text-white";
            const disabledClasses =
              "text-gray-500 cursor-not-allowed opacity-50";

            const classes = `${baseClasses} ${
              item.disabled
                ? disabledClasses
                : item.current
                  ? activeClasses
                  : inactiveClasses
            }`;

            if (item.disabled) {
              return (
                <div key={item.name} className={classes}>
                  <Icon className="mr-3 h-6 w-6 flex-shrink-0" />
                  {item.name}
                  <span className="ml-auto text-xs">(Soon)</span>
                </div>
              );
            }

            return (
              <Link key={item.name} to={item.href} className={classes}>
                <Icon className="mr-3 h-6 w-6 flex-shrink-0" />
                {item.name}
              </Link>
            );
          })}
        </nav>
      </div>
    </div>
  );
};

export default Layout;
